import React, { useState, useEffect, useContext } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Box, Stack } from '@mui/material';
import { Button, TextField, TextView } from '@atoms';
import { DateRangePicker, Range } from 'react-date-range';
import { DateTime } from 'luxon';
import { materialTheme, TableContext, useZazuAuditLogs, useZazuConversation } from '@particles';
import { columns, generateRows, ConversationPanel } from './particles';
import { BillingAccountSelector, Table } from '@organisms';

import Popover from '@mui/material/Popover';
import EventIcon from '@mui/icons-material/EventOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

const ZazuAuditTrails: React.FC<RouteComponentProps> = () => {
  const [start, setStart] = useState<Date>(DateTime.local().minus({ days: 7 }).toJSDate());
  const [end, setEnd] = useState<Date>(new Date());
  const [contactId, setContactId] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;
  const [location, setLocation] = React.useState<BillingAccount | null>();
  const [range, setRange] = useState<Range[]>([
    {
      startDate: start,
      endDate: end,
      key: 'selection'
    }
  ]);

  const {
    state: {
      zazuAuditLogsPagination: { page, perPage, orderBy }
    },
    dispatch
  } = useContext(TableContext);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const date = new Date();
    const start = range[0]?.startDate ? range[0].startDate : date;
    const end = range[0]?.endDate ? range[0].endDate : date;

    const newEnd = new Date(end);
    newEnd.setHours(23);
    newEnd.setMinutes(59);
    newEnd.setSeconds(59);

    const newStart = new Date(start);
    newStart.setHours(0);
    newStart.setMinutes(0);
    newStart.setSeconds(0);

    setStart(start);
    setEnd(newEnd);
    // onSelectRange && onSelectRange(start, newEnd);
  }, [range]);

  const handleRefreshLogs = async () => {
    dispatch({
      type: `set`,
      key: `zazuAuditLogsPagination`,
      value: { page: 1, perPage: perPage }
    });
    zazuAuditLogsRefetch();
  };

  const {
    data,
    isLoading,
    refetch: zazuAuditLogsRefetch
  } = useZazuAuditLogs({
    locationId: location?.locationId,
    contactId,
    type: 'response',
    page,
    pageSize: perPage,
   startDate: DateTime.fromJSDate(start).toISO({
    suppressMilliseconds: true,
    includeOffset: false
  }),
   endDate: DateTime.fromJSDate(end).toISO({
    suppressMilliseconds: true,
    includeOffset: false
  })
  });

  // The stored conversation is keyed by (location, contact) — the same two
  // filters this screen already carries — so it reads from them directly rather
  // than adding a second set of controls.
  const { data: conversation, isLoading: isConversationLoading } = useZazuConversation({
    locationId: location?.locationId,
    contactId
  });

  const rows = generateRows(data?.data?.data ?? []);

  return (
    <Box m={4}>
      {/* Header Section */}
      <Box sx={{ mt: 1, mb: 3 }}>
        {/* Search and Filter Controls */}
        <Stack direction={'row'} spacing={2} sx={{ mb: 2, mt: 2, flexWrap: 'wrap', rowGap: 2 }} alignItems={'center'}>
          {/* Core Contact ID Search */}
          <TextField
            label=""
            name={'contactId'}
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
            placeholder={'Search by Core Contact ID'}
            sx={{ minWidth: 250 }}
          />
          {/* Date Range Picker */}

          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}>
            <DateRangePicker
              onChange={(item) => setRange([item.selection])}
              moveRangeOnFirstSelection={false}
              months={2}
              ranges={range}
              direction="horizontal"
            />
          </Popover>

          <Button
            aria-describedby={id}
            sx={{
              paddingLeft: 5,
              color: materialTheme.palette.text.primary,
              mr: 2,
              minWidth: 320,
              whiteSpace: 'nowrap'
            }}
            startIcon={<EventIcon />}
            variant={'outlined'}
            onClick={handleClick}>
            <TextView $fontWeight={400} sx={{ whiteSpace: 'nowrap' }}>
              {DateTime.fromJSDate(range[0].startDate ? range[0]?.startDate : new Date()).toFormat(
                'LLLL, dd yyyy'
              )}
              {` - `}
              {DateTime.fromJSDate(range[0].endDate ? range[0]?.endDate : new Date()).toFormat(
                'LLLL, dd yyyy'
              )}
            </TextView>
          </Button>

          <BillingAccountSelector
            type="c"
            status='both'
            caller="audit-trails"
            onSelected={function (core: BillingAccount | null): void {
              setLocation(core);
            }}

          />

          {/* Refresh Logs Button */}
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefreshLogs}
            sx={{ ml: 2 }}
            variant={'contained'}>
            Refresh Logs
          </Button>
        </Stack>
      </Box>


      <ConversationPanel
        locationId={location?.locationId}
        contactId={contactId}
        detail={conversation?.data}
        isLoading={isConversationLoading}
      />

      <Table
        name="zazu-audit-logs"
        columns={columns}
        data={rows}
        isLoading={isLoading}
        pagination={data?.data}
        persistColumnPrefs
      />
    </Box>
  );
};

export default ZazuAuditTrails;
